import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

/**
 * Simple debounce function
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Hook pour vérifier la disponibilité d'une immatriculation en temps réel
 * @param {string} immatriculation - L'immatriculation à vérifier
 * @param {boolean} enabled - Si la vérification est activée
 * @returns {Object} { isChecking, isAvailable, message }
 */
export const usePlateAvailability = (immatriculation, enabled = true) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [message, setMessage] = useState('');
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled || !immatriculation || immatriculation.length < 5) {
      setIsAvailable(null);
      setMessage('');
      return;
    }

    setIsChecking(true);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout (debounce)
    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/vehicules/check-plate/${encodeURIComponent(immatriculation)}`);
        setIsAvailable(response.data.available);
        setMessage(response.data.message);
      } catch (error) {
        console.error('Error checking plate availability:', error);
        setIsAvailable(null);
        setMessage('');
      } finally {
        setIsChecking(false);
      }
    }, 500);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [immatriculation, enabled]);

  return { isChecking, isAvailable, message };
};
